import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import './HowItWorks.css';
import step1card from '../assets/howitworks/HOWITWORKS_CARD_STEP1.webp';
import step2card from '../assets/howitworks/HOWITWORKS_CARD_STEP2.webp';
import step3card from '../assets/howitworks/HOWITWORKS_CARD_STEP3.webp';
import step4card from '../assets/howitworks/HOWITWORKS_CARD_STEP4.webp';
import step1icon from '../assets/icons/Step1_Icon.svg';
import step2icon from '../assets/icons/Step2_Icon.svg';
import step3icon from '../assets/icons/Step3_Icon.svg';
import step4icon from '../assets/icons/Step4_Icon.svg';


// Register GSAP plugins
gsap.registerPlugin(Draggable, InertiaPlugin);

const HowItWorks = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [loadedIcons, setLoadedIcons] = useState({});

  // Intersection Observer for the section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load background image when it comes into view
  const handleImageLoad = (index) => {
    setLoadedImages(prev => ({ ...prev, [index]: true }));
  };

  // Load icon when it comes into view
  const handleIconLoad = (index) => {
    setLoadedIcons(prev => ({ ...prev, [index]: true }));
  };
  const steps = [
    {
      number: "Analyse",
      title: "Dein Business verstehen",
      description: "Wir starten mit einem strukturierten Deep Dive in deine Abläufe. Von Vertrieb über Backoffice bis zum Kundenservice: Wir identifizieren Bottlenecks, repetitive Aufgaben und ungenutzte Potenziale. Das Ergebnis ist ein klarer Überblick darüber, wo KI wirklich Wirkung entfalten kann. Faktenbasiert, statt nur auf Hypothesen.",
      icon: step1icon
    },
    {
      number: "Roadmap",
      title: "Klare KI-Strategie und Proof of Concept",
      description: "Auf Basis der Analyse erstellen wir eine maßgeschneiderte Roadmap: Wir zeigen, welche Prozesse automatisiert werden können, wo Umsatzpotenziale liegen und wie dein Team sofort entlastet wird. Anschließend setzen wir einen Proof of Concept auf, um die Automatisierung fehlerfrei zu testen, bevor sie vollständig implementiert wird.",
      icon: step2icon
    },
    {
      number: "Integration",
      title: "KI-Blocks nahtlos implementieren",
      description: "Nach Abschluss der erfolgreichen Testphase, setzen wir die passenden KI-Blocks direkt in deine Systeme ein. Schnell, reibungslos und ohne komplizierte Umstellungen. Alles läuft reibungslos im Hintergrund, ohne dass du bestehende Strukturen auf den Kopf stellen musst. So entsteht Schritt für Schritt dein KI-Stack, der sofort Mehrwert bringt.",
      icon: step3icon
    },
    {
      number: "Skalierung",
      title: "Ergebnisse messen und skalieren",
      description: "Nach der Implementierung geht es um Wachstum. Wir messen die erzielten Effekte, optimieren kontinuierlich und erweitern dein Setup um neue KI-Blocks. So stellst du sicher, dass dein Unternehmen nicht nur Kosten spart, sondern auch mehr Kunden gewinnt und nachhaltig skalieren kann.",
      icon: step4icon
    }
  ];

  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const nextButtonRef = useRef(null);
  const prevButtonRef = useRef(null);
  const draggableRef = useRef(null);
  const [slideWidth, setSlideWidth] = useState(620); // 600px width + 20px margin

  // Update slide width based on screen size
  useEffect(() => {
    const updateSlideWidth = () => {
      // On mobile, use 90% of viewport width minus padding
      if (window.innerWidth < 768) {
        const mobileWidth = window.innerWidth * 0.9;
        setSlideWidth(mobileWidth + 20); // Add margin
      } else {
        // Desktop width
        setSlideWidth(620); // 600px width + 20px margin
      }
    };

    // Initial calculation
    updateSlideWidth();

    // Add resize listener
    window.addEventListener('resize', updateSlideWidth);

    return () => {
      window.removeEventListener('resize', updateSlideWidth);
    };
  }, []);

  useEffect(() => {
    // Initialize slider after component mounts
    const initSlider = () => {
      const container = containerRef.current;
      const slider = sliderRef.current;
      const nextButton = nextButtonRef.current;
      const prevButton = prevButtonRef.current;
      
      if (!container || !slider || !nextButton || !prevButton) {
        return;
      }

      // Set initial state
      let currentIndex = 0;
      const totalSlides = steps.length;
      
      // Position slides horizontally
      const slides = slider.children;
      for (let i = 0; i < slides.length; i++) {
        slides[i].style.position = 'absolute';
        slides[i].style.left = `${i * slideWidth}px`;
      }
      
      // Set container width
      slider.style.width = `${totalSlides * slideWidth}px`;
      
      // Create draggable with enhanced touch support
      const draggable = Draggable.create(slider, {
        type: "x",
        bounds: {
          minX: -(totalSlides - 1) * slideWidth,
          maxX: 0
        },
        inertia: true,
        // Enhanced touch settings for smoother mobile experience
        allowNativeTouchScrolling: true,
        dragClickables: true,
        edgeResistance: 0.8,
        throwProps: true,
        snap: {
          x: (value) => Math.round(value / slideWidth) * slideWidth
        },
        onDragStart: function() {
          // Add visual feedback during drag
          gsap.set(slider, { scale: 1 });
        },
        onDragEnd: function() {
          // Reset scale after drag
          gsap.to(slider, { scale: 1, duration: 0.2 });
          
          // Calculate current index based on position
          const endPosition = this.x;
          currentIndex = Math.round(Math.abs(endPosition) / slideWidth);
          currentIndex = Math.max(0, Math.min(currentIndex, totalSlides - 1));
        }
      })[0];
      
      draggableRef.current = draggable;
      
      // Button handlers
      const goToNext = () => {
        if (currentIndex < totalSlides - 1) {
          currentIndex++;
          gsap.to(slider, {
            x: -currentIndex * slideWidth,
            duration: 0.7,
            ease: "power2.out"
          });
        }
      };
      
      const goToPrev = () => {
        if (currentIndex > 0) {
          currentIndex--;
          gsap.to(slider, {
            x: -currentIndex * slideWidth,
            duration: 0.7,
            ease: "power2.out"
          });
        }
      };
      
      // Add event listeners
      nextButton.addEventListener('click', goToNext);
      prevButton.addEventListener('click', goToPrev);
      
      // Add keyboard navigation support
      const handleKeyDown = (e) => {
        if (e.key === 'ArrowRight') {
          goToNext();
        } else if (e.key === 'ArrowLeft') {
          goToPrev();
        }
      };
      
      window.addEventListener('keydown', handleKeyDown);
      
      // Cleanup function
      return () => {
        nextButton.removeEventListener('click', goToNext);
        prevButton.removeEventListener('click', goToPrev);
        window.removeEventListener('keydown', handleKeyDown);
      };
    };

    // Initialize after a short delay to ensure DOM is ready
    const timer = setTimeout(initSlider, 100);
    
    // Cleanup function
    return () => {
      clearTimeout(timer);
      if (draggableRef.current) {
        draggableRef.current.kill();
      }
    };
  }, [steps, slideWidth]);

  return (
    <section className="relative min-h-[75vh] bg-[#010003] text-white py-20 px-4 pt-[150px] overflow-hidden">
      {/* Blur effect elements */}
      <div className="absolute inset-0 z-0 overflow-visible">
        <div className="absolute bottom-[5%] left-[8%] w-[30vw] h-[50vh] rounded-full bg-[#F5EBEB] opacity-5 blur-3xl"></div>
        <div className="absolute top-[30%] left-[35%] w-[25vw] h-[30vh] rounded-full bg-[#F5EBEB] opacity-5 blur-3xl"></div>
      </div>
      <div className="max-w-[1280px] mx-auto">
        {/* Header Section */}
        <div className="mb-[0px]">
          {/*<span className="font-['Geist'] font-light text-sm text-white tracking-[0.02em] pb-3 block">
            WIE BLCKS ARBEITET
          </span>*/}
          {/*<h2 className="font-['CalSans'] font-medium text-[48px] text-white tracking-[0.02em] text-left mb-6">
            Consulting that turns into action.
          </h2>*/}
          <h2 className="font-['CalSans'] font-medium text-2xl sm:text-[48px] text-white tracking-[0.02em] text-left mb-6">
            Beratung, die in<span className="sm:hidden"><br/></span><span className="hidden sm:inline"> </span>Handlung umsetzt.
          </h2>
          <p className="font-['Geist'] font-normal text-sm sm:text-base text-[#ADB0B9] max-w-[700px] pb-[0px]">
            Keine endlosen Reports, sondern direkte Umsetzung. Wir verbinden strategische Beratung mit technischer Umsetzung. So weißt du nicht nur, was möglich ist, sondern siehst schnell echte Resultate in deinem Tagesgeschäft.
          </p>
        </div>

        {/* Slider Controls */}
        <div className="flex gap-x-5 justify-end mb-6 relative z-10">
          <button
            ref={prevButtonRef}
            className="group relative w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#22242A] transition-all duration-300 hover:shadow-[0px_2px_40px_0px_rgba(76,83,103,.9),0px_4px_8px_0px_rgba(0,0,0,.25)] after:absolute after:-inset-2 before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(180deg,#505562_0%,#22242A_100%)] before:opacity-0 before:duration-300 hover:before:opacity-100"
            aria-label="Previous slide"
          >
            <span className="sr-only">prev slide</span>
            <div className="relative flex items-center justify-center right-px -scale-x-100">
              <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                <path
                  className="stroke-white"
                  d="M1 1L7 7L1 13"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  className="stroke-[url(#gradient)] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  d="M1 1L7 7L1 13"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <defs>
                  <linearGradient id="gradient" x1="4" y1="1" x2="4" y2="13" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white"></stop>
                    <stop offset="1" stopColor="#999999"></stop>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </button>
          <button
            ref={nextButtonRef}
            className="group relative w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#22242A] transition-all duration-300 hover:shadow-[0px_2px_40px_0px_rgba(76,83,103,.9),0px_4px_8px_0px_rgba(0,0,0,.25)] after:absolute after:-inset-2 before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(180deg,#505562_0%,#22242A_100%)] before:opacity-0 before:duration-300 hover:before:opacity-100"
            aria-label="Next slide"
          >
            <span className="sr-only">next slide</span>
            <div className="relative flex items-center justify-center left-px">
              <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                <path
                  className="stroke-white"
                  d="M1 1L7 7L1 13"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  className="stroke-[url(#gradient)] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  d="M1 1L7 7L1 13"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <defs>
                  <linearGradient id="gradient" x1="4" y1="1" x2="4" y2="13" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white"></stop>
                    <stop offset="1" stopColor="#999999"></stop>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </button>
        </div>

        {/* Slider - REMOVED overflow-hidden from slider-wrap */}
        <div className="main w-full relative h-[450px]">
          <div 
            className="slider-wrap w-full h-full"
            // Add touch action to prevent conflicts with drag
            style={{ touchAction: 'pan-x' }}
          >
            <div 
              ref={containerRef}
              className="slider-list w-full h-full relative"
            >
              <div 
                ref={sliderRef}
                className="absolute top-0 left-0 h-full"
              >
                {steps.map((step, index) => (
                  <div 
                    key={index}
                    className="absolute top-0 h-full max-sm:h-[350px]"
                    style={{ width: `${slideWidth - 20}px`, marginRight: '20px' }}
                  >
                    <div 
                      className="rounded-2xl w-full h-[400px] max-sm:h-[350px] relative overflow-hidden transition-all duration-500 ease-in-out transform hover:shadow-xl"
                      style={{
                        backgroundImage: `url(${[step1card, step2card, step3card, step4card][index]})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }}
                    >
                      {/* Semi-transparent overlay to ensure text remains readable */}
                      <div className="absolute inset-0 bg-black/20"></div>
                      {/* Top Left Icon */}
                      <div className="absolute top-5 left-5 z-10">
                        {loadedIcons[index] ? (
                          <img 
                            src={step.icon} 
                            alt={`Step ${index + 1}`}
                            className="w-10 h-10"
                            loading="lazy"
                          />
                        ) : (
                          <div 
                            className="w-10 h-10 bg-gray-800 animate-pulse rounded"
                            style={{ minHeight: '40px' }}
                          >
                            <img 
                              src={step.icon} 
                              alt="" 
                              className="w-0 h-0" 
                              onLoad={() => handleIconLoad(index)}
                            />
                          </div>
                        )}
                      </div>

                      {/* Vertical Line */}
                      <div className="absolute top-0 bottom-0 left-20 w-px" style={{ backgroundColor: 'rgba(120, 124, 135, 0.25)' }}></div>

                      {/* Vertical Text */}
                      <div className="absolute bottom-5 left-5 w-6 h-32 overflow-visible z-10">
                        <span className="absolute bottom-0 left-0 font-['CalSans'] text-[26px] font-normal tracking-[0.02em] text-white transform -rotate-90 origin-bottom-left whitespace-nowrap translate-x-10 translate-y-0">
                          {step.number}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="absolute top-8 left-[100px] right-5 z-10">
                        <h3 className="font-['Geist'] font-normal text-[18px] text-white tracking-[-0.02em] mb-3.5">
                          {step.title}
                        </h3>
                        <p className="font-['Geist'] font-normal text-[13px] text-[#787c87] tracking-[-0.02em] max-w-[475px] max-sm:text-xs">
                          {step.description}
                        </p>
                      </div>

                      {/* Bottom Right Text */}
                      <div className="absolute bottom-[30px] right-5 w-[calc(100%-100px)] z-10">
                        <p className="font-['Geist'] font-normal text-[10px] text-[#787c87] text-right tracking-[0.01em] opacity-25">
                          {index === 0 ? '25%' : index === 1 ? '50%' : index === 2 ? '75%' : '100%'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;